import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ticketService from '../../services/ticketService';
import { getTicketConversationPage } from '../../utils/ticketActivity';
import { isCollaborativeTicket } from '../../utils/ticketRequests';
import TicketRequestAudit from './TicketRequestAudit';
import TicketActionDialog from './TicketActionDialog';

const PAGE_SIZE = 20;
const getId = value => String(value?._id || value || '');
const getDate = value => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const TicketConversation = ({ ticket, currentUserId, getEntryTitle, getEntryChanges, onImagePreview, children }) => {
  const [filter, setFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activeDetails, setActiveDetails] = useState(null);
  const scrollRef = useRef(null);
  const olderScrollRef = useRef(null);
  const { messages, total, hasOlder } = useMemo(() => getTicketConversationPage(ticket.activity_logs, filter, visibleCount),
    [ticket.activity_logs, filter, visibleCount]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const previous = olderScrollRef.current;
    element.scrollTop = previous ? previous.top + element.scrollHeight - previous.height : element.scrollHeight;
    olderScrollRef.current = null;
  }, [ticket._id, filter, visibleCount, total]);

  const loadOlder = () => {
    const element = scrollRef.current;
    if (element) olderScrollRef.current = { height: element.scrollHeight, top: element.scrollTop };
    setVisibleCount(count => count + PAGE_SIZE);
  };

  return <section className="card ticket-conversation" aria-label="Ticket conversation">
    <header className="ticket-chat-heading">
      <div><h3>Conversation</h3><span>{total} {total === 1 ? 'entry' : 'entries'}</span></div>
      <div className="ticket-activity-filters" role="group" aria-label="Filter conversation">
        {[['all', 'All'], ['comments', 'Comments'], ['operations', 'Operations']].map(([value, label]) => (
          <button key={value} type="button" className={`btn ${filter === value ? 'btn-primary' : 'btn-secondary'}`}
            aria-pressed={filter === value} onClick={() => { setFilter(value); setVisibleCount(PAGE_SIZE); }}>{label}</button>
        ))}
      </div>
    </header>
    <div className="ticket-chat-messages" ref={scrollRef} role="region" aria-label="Conversation messages" tabIndex={0}>
      {hasOlder && <button type="button" className="btn btn-secondary ticket-chat-load" onClick={loadOlder}>Load older messages</button>}
      {!total && <p className="ticket-chat-empty">{filter === 'operations' ? 'No operations yet.' : 'No messages yet.'}</p>}
      {messages.map((entry, index) => {
        const title = getEntryTitle(entry);
        const changes = getEntryChanges(entry);
        const date = getDate(entry.created_at);
        const previousDate = getDate(messages[index - 1]?.created_at);
        const images = entry.images || [];
        const hasMessage = !!entry.note?.trim() || images.length > 0 || entry.action === 'commented';
        const isOperation = entry.action !== 'commented';
        const isOwn = !isOperation && !!currentUserId && getId(entry.actor_id) === getId(currentUserId);
        const hasAudit = ['request_updated', 'request_handoff', 'request_returned', 'request_review', 'request_progress'].includes(entry.action)
          || (entry.action === 'created' && isCollaborativeTicket(ticket));
        const showChanges = !hasAudit && entry.action !== 'commented' && changes.length > 0;
        const detailsTitle = entry.action === 'created' ? 'Original request' : 'Changes';
        const time = <time dateTime={date?.toISOString()} title={date?.toLocaleString()}>{date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>;
        return <React.Fragment key={entry._id || `${entry.created_at}-${entry.action}-${index}`}>
          {date && date.toDateString() !== previousDate?.toDateString() && <div className="ticket-chat-date">
            {date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>}
          <article className={`ticket-chat-item${isOwn ? ' is-own' : ''}${isOperation ? ' is-event' : ''}`}>
            {!isOperation && <span className="ticket-chat-avatar" aria-hidden="true">{title.actorName.slice(0, 1).toUpperCase()}</span>}
            <div className="ticket-chat-content">
              {isOperation ? <div className="ticket-chat-operation">
                <div className="ticket-chat-operation-meta"><strong>{title.actorName}</strong>{time}</div>
                <div className="ticket-chat-operation-description"><span>{title.text}</span>
                  {(hasAudit || showChanges) && <button type="button" className="ticket-chat-info" aria-label={`View ${detailsTitle.toLowerCase()}`}
                    title={`View ${detailsTitle.toLowerCase()}`} onClick={() => setActiveDetails({ entry, changes, hasAudit, title: detailsTitle })}>
                    <span aria-hidden="true">i</span>
                  </button>}
                </div>
              </div> : <div className="ticket-chat-meta"><strong>{title.actorName}</strong>{time}</div>}
              {hasMessage && <div className="ticket-chat-bubble">
                {entry.note && <p>{entry.note}</p>}
                {images.length > 0 && <div className="ticket-chat-images">
                  {images.map((image, imageIndex) => <button key={image.object_key || image.file_path || imageIndex} type="button"
                    onClick={() => onImagePreview(images, imageIndex)} title={image.original_name || 'Preview image'}>
                    <img src={ticketService.getTicketImageUrl(image)} alt={image.original_name || 'Ticket image'} loading="lazy" />
                    <span>{image.original_name || image.file_name || 'Image'}</span>
                  </button>)}
                </div>}
              </div>}
            </div>
          </article>
        </React.Fragment>;
      })}
    </div>
    {children}
    {activeDetails && <TicketActionDialog title={activeDetails.title} onClose={() => setActiveDetails(null)}>
      <div className="ticket-information-panel">
        <header className="ticket-information-heading"><h3>{activeDetails.title}</h3>
          <button type="button" className="btn btn-secondary" onClick={() => setActiveDetails(null)}>Close</button>
        </header>
        <p className="ticket-muted">{getEntryTitle(activeDetails.entry).actorName}
          {activeDetails.entry.created_at && ` · ${getDate(activeDetails.entry.created_at)?.toLocaleString() || ''}`}</p>
        {activeDetails.hasAudit ? <TicketRequestAudit entry={activeDetails.entry} /> : <dl className="ticket-audit-rows">
          {activeDetails.changes.map((change, index) => <div className="ticket-audit-row" key={index}>
            <dt>{change.label}</dt><dd>{change.from ? `${change.from} → ${change.to}` : change.text || change.to}</dd>
          </div>)}
        </dl>}
      </div>
    </TicketActionDialog>}
  </section>;
};

export default TicketConversation;
